import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Journey } from 'src/common/interfaces/journey.interface';
import { PitchChatMessages } from 'src/editor/content-creation/talk-to-journey/pitch-chat-messages';
import { EnhancedChatMessages } from 'src/editor/content-creation/talk-to-journey/enhanced-messages';
import { useEnhancedChatStore } from 'src/editor/content-creation/talk-to-journey/enhanced-store';

interface PlayerAiAssistantPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  journey: Journey;
  buttonRef: React.RefObject<HTMLDivElement>;
}

export const PlayerAiAssistantPopover = ({ 
  isOpen, 
  onClose, 
  journey, 
  buttonRef 
}: PlayerAiAssistantPopoverProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { initializeFromJourney } = useEnhancedChatStore();

  // Initialize the enhanced chat store with journey data when popover opens for neue mode
  useEffect(() => {
    if (isOpen && journey.is_neue) {
      initializeFromJourney(journey);
    }
  }, [isOpen, journey, initializeFromJourney]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) return null;

  const buttonRect = buttonRef.current?.getBoundingClientRect();
  if (!buttonRect) return null;

  const popoverStyle = {
    position: 'fixed' as const,
    top: buttonRect.bottom + 8,
    right: window.innerWidth - buttonRect.right,
    zIndex: 1000,
  };

  return createPortal(
    <div
      ref={popoverRef}
      style={popoverStyle}
      className="bg-white border border-gray-200 rounded-lg shadow-lg w-96 h-[600px] flex flex-col"
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">AI Assistant</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-hidden">
        {journey.is_neue ? (
          <EnhancedChatMessages
            opened={isOpen}
            journeyUUID={journey.uuid}
            assistantName="Journey Assistant"
            personality=""
            onSourceClick={() => {}}
            journey={journey}
            inEditor={false}
          />
        ) : (
          <PitchChatMessages
            opened={isOpen}
            journeyUUID={journey.uuid}
            assistantName="Journey Assistant"
            personality=""
            onSourceClick={() => {}}
            journey={journey}
          />
        )}
      </div>
    </div>,
    document.body
  );
}; 